class LoadMore {
    private readonly $documentObject: Document
    private readonly $orderList: HTMLUListElement
    private readonly $loadMoreLink: HTMLAnchorElement
    private readonly $loadMoreErrorBanner: HTMLDivElement
    private readonly apiUrl: string
    private readonly QUERY_PARAM_PAGE_NUMBER: string = 'pageNumber'
    private readonly QUERY_PARAM_OFFSET: string = 'offset'
    private readonly HEADER_KEY_MORE_ITEMS_AVAILABLE: string = 'X-More-Items-Available'
    private readonly HEADER_KEY_NEXT_OFFSET: string = 'X-Next-Offset'
    private readonly LOAD_MORE_LINK_CLASS_HIDDEN: string = 'con_support-start__load-more-link--hidden'
    private readonly LOAD_MORE_ERROR_BANNER_CLASS_VISIBLE: string = 'con_support-start__error-banner--visible'
    private readonly QUERY_PARAM_PREV_URL: string = 'focussedDialogPrevUrl'
    private readonly IRRELEVANT_BASE_URL: string = 'https:/*      */
    private readonly DEFAULT_PAGE_NUMBER = 0
    private readonly DEFAULT_NEXT_OFFSET = 0
    private readonly REGEX_FOR_UNENCODED_PAGE_NUMBER: RegExp = /pageNumber=[0-9]+/
    private readonly domParser: DOMParser
    private currentPageNumber: PageNumber
    private currentOffset: number

    private constructor(documentObject: Document, orderList: HTMLUListElement, loadMoreLink: HTMLAnchorElement) {
        this.$documentObject = documentObject
        this.$orderList = orderList
        this.$loadMoreLink = loadMoreLink
        this.$loadMoreErrorBanner = this.$documentObject.querySelector('[data-js-hook="con_support__load-more-error-banner"]')!
        this.apiUrl = this.$loadMoreLink.dataset.jsApiEndpoint!
        this.currentPageNumber = this.retrieveCurrentPageNumberFromUri()
        this.currentOffset = this.retrieveCurrentNextOffsetFromModel()
        this.domParser = new DOMParser()
    }

    private bindLoadMoreFunctionality() {
        this.$loadMoreLink.addEventListener('click', (event) => {
            event.preventDefault()
            this.hideLoadMoreErrorBanner()
            this.hideLoadMoreLink()
            const newOrderListResponse: Promise<OrderListResponse> = this.fetchNextPage()
            newOrderListResponse.then(
                (response) => {
                    this.$orderList.append(...response.orderListItems)
                    if (response.moreItemsAvailable) {
                        this.showLoadMoreLink()
                    } else {
                        this.hideLoadMoreLink()
                    }
                    this.updateCurrentOffset(response.nextOffset, response.orderListItems.length)
                    this.incrementCurrentPageNumber()
                    this.updateAllFocussedDialogQueryParams(this.$orderList)
                },
                () => {
                    this.showLoadMoreErrorBanner()
                    this.showLoadMoreLink()
                }
            )
        })
    }

    private async fetchNextPage(): Promise<OrderListResponse> {
        const response = await fetch(this.apiUrl + this.assembleApiUrlQueryString())
        if (response.ok) {
            const returnedString = await response.text()
            const parsedHtml: Document = this.domParser.parseFromString(returnedString, 'text/html')
            const orderPositionList: NodeList = parsedHtml.querySelectorAll('[data-js-hook="con_support__order-list-item"]')
            const moreItemsAvailable: boolean = response.headers.get(this.HEADER_KEY_MORE_ITEMS_AVAILABLE) === 'true'
            const nextOffset = this.parseNextOffset(response.headers.get(this.HEADER_KEY_NEXT_OFFSET))
            return new OrderListResponse(orderPositionList, moreItemsAvailable, nextOffset)
        } else {
            const emptyNodeList = this.$documentObject.querySelectorAll('somethingnotexistent')
            return Promise.reject(new OrderListResponse(emptyNodeList, false, null))
        }
    }

    private assembleApiUrlQueryString() {
        return '?' + new URLSearchParams({
            [this.QUERY_PARAM_PAGE_NUMBER]: String(this.currentPageNumber.value + 1),
            [this.QUERY_PARAM_OFFSET]: String(this.currentOffset)
        })
    }

    private incrementCurrentPageNumber() {
        this.currentPageNumber = new PageNumber(this.currentPageNumber.value + 1)
        this.syncUriWithPageNumber()
        this.updatePageNumberWithinDialogPrevUriForExistingOrderPositions()
    }

    private updateCurrentOffset(nextOffset: number | null, loadedItemsCount: number) {
        if (nextOffset !== null) {
            this.currentOffset = nextOffset
            return
        }
        this.currentOffset += loadedItemsCount
    }

    private parseNextOffset(nextOffsetHeader: string | null): number | null {
        const nextOffset = Number.parseInt(nextOffsetHeader ?? '', 10)
        return Number.isNaN(nextOffset) ? null : nextOffset
    }

    private syncUriWithPageNumber() {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set(this.QUERY_PARAM_PAGE_NUMBER, String(this.currentPageNumber.value))
        searchParams.set(this.QUERY_PARAM_OFFSET, String(this.currentOffset))
        const updatedRelativePathAndQueryString = window.location.pathname + '?' + searchParams.toString()
        window.history.replaceState({}, document.title, updatedRelativePathAndQueryString)
    }

    private updatePageNumberWithinDialogPrevUriForExistingOrderPositions() {
        Array.from(this.$orderList.children).forEach(orderItem => {
            const orderItemLink: HTMLAnchorElement = orderItem.querySelector('[data-js-hook="con_support__order-list-item-link"]')!
            const urlToBeUpdated = new URL(orderItemLink.href, this.IRRELEVANT_BASE_URL)
            const oldPrevUrl = urlToBeUpdated.searchParams.get(this.QUERY_PARAM_PREV_URL)
            if (oldPrevUrl) {
                urlToBeUpdated.searchParams.set(this.QUERY_PARAM_PREV_URL, this.createStringWithUpdatedPageNumber(oldPrevUrl))
                orderItemLink.href = urlToBeUpdated.pathname + urlToBeUpdated.search
            }
        })
    }

    private createStringWithUpdatedPageNumber(stringPossiblyWithPageNumber: string): string {
        const stringWithUpdatedOrCreatedPageNumber = this.REGEX_FOR_UNENCODED_PAGE_NUMBER.test(stringPossiblyWithPageNumber)
            ? stringPossiblyWithPageNumber.replace(this.REGEX_FOR_UNENCODED_PAGE_NUMBER, 'pageNumber=' + this.currentPageNumber.value)
            : stringPossiblyWithPageNumber + '&pageNumber=' + this.currentPageNumber.value
        return stringWithUpdatedOrCreatedPageNumber
    }

    private retrieveCurrentPageNumberFromUri(): PageNumber {
        const urlParams = new URLSearchParams(window.location.search)
        let currentPageNumber: number = parseInt(urlParams.get(this.QUERY_PARAM_PAGE_NUMBER)!)
        currentPageNumber = isNaN(currentPageNumber) ? this.DEFAULT_PAGE_NUMBER : currentPageNumber
        return new PageNumber(currentPageNumber)
    }
    private retrieveCurrentNextOffsetFromModel(): number {
        const nextOffset = Number.parseInt(this.$loadMoreLink.dataset.jsNextOffset ?? '', 10)
        return Number.isNaN(nextOffset) ? this.DEFAULT_NEXT_OFFSET : nextOffset
    }

    private showLoadMoreLink(): void {
        this.$loadMoreLink.classList.remove(this.LOAD_MORE_LINK_CLASS_HIDDEN)
    }

    private hideLoadMoreLink() {
        this.$loadMoreLink.classList.add(this.LOAD_MORE_LINK_CLASS_HIDDEN)
    }

    private showLoadMoreErrorBanner(): void {
        this.$loadMoreErrorBanner.classList.add(this.LOAD_MORE_ERROR_BANNER_CLASS_VISIBLE)
    }

    private hideLoadMoreErrorBanner() {
        this.$loadMoreErrorBanner.classList.remove(this.LOAD_MORE_ERROR_BANNER_CLASS_VISIBLE)
    }

    private updateAllFocussedDialogQueryParams(targetContainer: HTMLElement) {
        window.o_global.eventQBus.emit(
            'assets.focusseddialog.init',
            window.location.search,
            window.location.href,
            targetContainer
        )
    }

    static initialize(documentObject: Document, orderList: HTMLUListElement, loadMoreLink: HTMLAnchorElement): LoadMore {
        const loadMore: LoadMore = new LoadMore(documentObject, orderList, loadMoreLink)
        loadMore.bindLoadMoreFunctionality()
        return loadMore
    }
}

export default LoadMore

class PageNumber {
    private readonly _value: number
    public constructor(pageNumber: number) {
        this._value = pageNumber
    }
    public get value() {
        return this._value
    }
}

class OrderListResponse {
    private readonly _orderListItems: NodeList
    private readonly _moreItemsAvailable: boolean
    private readonly _nextOffset: number | null

    public constructor(orderListItems: NodeList, moreItemsAvailable: boolean, nextOffset: number | null) {
        this._orderListItems = orderListItems
        this._moreItemsAvailable = moreItemsAvailable
        this._nextOffset = nextOffset
    }

    public get orderListItems() {
        return this._orderListItems
    }
    public get moreItemsAvailable() {
        return this._moreItemsAvailable
    }
    public get nextOffset() {
        return this._nextOffset
    }
}
